import { JSX, RefObject, useEffect, useRef, useState } from 'react';
import { usePermissions } from 'react-admin';
import { ChatActivity, ChatMessage } from '../context/ChatBotContextProvider';
import { OpenInHqButton } from './OpenInHqButton';
import { MapCard } from './MapCard';
import { MarkdownMessage } from './MarkdownMessage';
import { ProposalCard } from './ProposalCard';
import { ConversionCard } from './ConversionCard';
import { ReportCard } from './ReportCard';
import { useWorkingStatus, WorkingStatus } from '../hooks/use-working-status';
import { CapturedStamp, isReplayed } from './CapturedStamp';

/** How close to the bottom still counts as "following along". */
const STICK_THRESHOLD_PX: number = 60;

interface TranscriptProps {
    messages: ChatMessage[];
    botName: string;
    loading: boolean;
    errorMessage: string;
    /** What the assistant is doing right now, or '' when it is doing nothing. */
    activity: ChatActivity;
}

export const ChatTranscript: (props: TranscriptProps) => JSX.Element =
    ({ messages, botName, loading, errorMessage, activity }): JSX.Element => {
        // Read here rather than in the card, so the card stays renderable on its own in a test.
        // Only the conversion card asks: everything else the assistant offers is either read-only
        // or goes through a form react-admin is already gating.
        const { permissions } = usePermissions();
        const scroller: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
        const [stuckToBottom, setStuckToBottom] = useState<boolean>(true);

        // Follow the reply as it streams, but stop the moment the reader scrolls up to read
        // something earlier. Unconditionally scrolling to the bottom on every delta makes the
        // transcript impossible to read while an answer is arriving.
        function handleScroll(): void {
            const node: HTMLDivElement | null = scroller.current;
            if (node === null) {
                return;
            }
            const distance: number = node.scrollHeight - node.scrollTop - node.clientHeight;
            setStuckToBottom(distance < STICK_THRESHOLD_PX);
        }

        useEffect((): void => {
            const node: HTMLDivElement | null = scroller.current;
            if (node !== null && stuckToBottom) {
                node.scrollTop = node.scrollHeight;
            }
        }, [messages, activity, stuckToBottom]);

        /**
         * Whether to show that the assistant is working on something.
         *
         * Only while nothing else on screen is already saying so, which rules out two cases.
         *
         * Once a reply starts arriving the words themselves are the sign that it is working, and
         * a second indicator under them says the same thing twice. And a tool chip that is
         * *running* is already a labelled, pulsing account of what is happening, better than any
         * word here because it names the actual lookup.
         *
         * What is left is the gap the chips do not cover: the wait before the first tool call,
         * the wait between a result coming back and the first word of the answer, and every
         * question that makes no tool call at all. That last one is the case the user reported,
         * and it was the whole wait with nothing on screen.
         */
        const streaming: boolean = messages.some(
            (message: ChatMessage): boolean => message.streaming === true
        );
        const looking: boolean = messages.some(
            (message: ChatMessage): boolean =>
                message.type === 'tool' && message.tool?.status === 'running'
        );
        const busy: boolean = activity !== '' && !streaming;
        const working: boolean = busy && !looking;
        // Driven by the whole exchange, not by whether the row is on screen. A question that makes
        // three lookups hides and shows this row three times, and restarting the clock and the
        // words on each of them would mean the count never passed three seconds and the word never
        // got past the first one - so a long answer full of lookups would look like a short one.
        const status: WorkingStatus = useWorkingStatus(busy);

        return (
            <div className="chat__transcript" ref={ scroller } onScroll={ handleScroll }>
                { loading && (
                    <div className="chat__notice">
                        <span className="notice__spinner" />
                        Connecting to { botName }
                    </div>
                ) }

                { errorMessage !== '' && (
                    <div className="chat__notice chat__notice--error">{ errorMessage }</div>
                ) }

                { !loading && errorMessage === '' && messages.length === 0 && (
                    <div className="chat__empty">
                        <p className="empty__title">Ask { botName } anything</p>
                        <p className="empty__hint">
                            It can read companies, customers, Noks, Minis and their results, and
                            search the web. It cannot change anything.
                        </p>
                    </div>
                ) }

                { messages.map((message: ChatMessage, index: number): JSX.Element => {
                    if (message.type === 'tool') {
                        const running: boolean = message.tool?.status === 'running';
                        return (
                            <div
                                className={ 'chat__tool' + (running ? ' is-running' : ' is-done') }
                                key={ 'tool_' + (message.tool?.id ?? index) }
                            >
                                <span className="tool__dot" />
                                <span className="tool__label">{ message.tool?.label }</span>
                                { message.tool?.summary && (
                                    <span className="tool__summary">{ message.tool.summary }</span>
                                ) }
                            </div>
                        );
                    }

                    if (message.type === 'map' && message.map) {
                        return (
                            <div
                                className={ 'chat__mapblock' + (isReplayed(message.capturedAt) ? ' is-replayed' : '') }
                                key={ 'map_' + index }
                            >
                                <span className="mapblock__label">
                                    { message.map.label }
                                    { /* HQ-11. A map is where the record was when it was drawn,
                                         and a conversation can be a fortnight old. */ }
                                    <CapturedStamp capturedAt={ message.capturedAt ?? 0 } />
                                </span>
                                <MapCard href={ message.map.embedUrl } label={ message.map.label } />
                            </div>
                        );
                    }

                    if (message.type === 'report' && message.report) {
                        return (
                            <ReportCard
                                key={ 'report_' + index }
                                report={ message.report }
                                capturedAt={ message.capturedAt }
                            />
                        );
                    }

                    if (message.type === 'proposal' && message.proposal) {
                        return (
                            <ProposalCard
                                key={ 'proposal_' + index }
                                proposal={ message.proposal }
                                capturedAt={ message.capturedAt }
                            />
                        );
                    }

                    if (message.type === 'conversion' && message.conversion) {
                        return (
                            <ConversionCard
                                key={ 'conversion_' + index }
                                conversion={ message.conversion }
                                permissions={ permissions }
                                capturedAt={ message.capturedAt }
                            />
                        );
                    }

                    if (message.type === 'navigate' && message.navigation) {
                        return (
                            <OpenInHqButton
                                key={ 'nav_' + index }
                                label={ message.navigation.label }
                                url={ message.navigation.url }
                                capturedAt={ message.capturedAt }
                            />
                        );
                    }

                    const mine: boolean = message.type === 'user';
                    return (
                        <div
                            className={ 'chat__turn' + (mine ? ' is-mine' : ' is-theirs') }
                            key={ 'message_' + index }
                        >
                            <span className="turn__author">{ message.author }</span>
                            <div
                                className={ 'turn__body' + (message.streaming ? ' is-streaming' : '') }
                            >
                                <MarkdownMessage
                                    content={ message.message }
                                    streaming={ message.streaming }
                                />
                            </div>
                        </div>
                    );
                }) }

                {/*
                  * The gap this fills. Between pressing Send and the first word there was
                  * nothing at all on screen: the composer's placeholder changed to "Waiting for
                  * a reply" at the very bottom of the panel and that was the whole of it, so a
                  * question that took twelve seconds to answer looked like one that had not been
                  * received. It sits where the reply itself will, which is where the reader is
                  * already looking.
                  */}
                { working && (
                    <div className="chat__working">
                        <span className="working__author">{ botName }</span>
                        <span className="working__body">
                            <span className="working__dots" aria-hidden="true">
                                <i /><i /><i />
                            </span>
                            {/*
                              * Hidden from assistive technology, all of it. The word changes every
                              * two seconds and the clock every second, and a live region would
                              * read the pair out over and over. The one steady sentence below is
                              * what gets announced instead.
                              */}
                            <span className="working__label" aria-hidden="true">
                                { activity === 'answering' ? 'writing a reply' : status.word }
                                { status.seconds > 0 && (
                                    <span className="working__clock">{ status.seconds }s</span>
                                ) }
                            </span>
                            <span className="working__announcement" aria-live="polite">
                                { botName } is working on it
                            </span>
                        </span>
                    </div>
                ) }
            </div>
        );
    };
